import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { mdiArrowDown, mdiArrowUp, mdiContentCopy, mdiContentSave, mdiDelete, mdiFlask, mdiPlus, mdiSitemapOutline } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Checkbox } from '../ui/checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'
import { Input } from '../ui/input'
import { Textarea } from '../ui/textarea'
import { PageHeader } from '../shared/PageHeader'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useCan } from '../../contexts/PermissionsContext'
import { PreviewSteps } from './ScenarioPanel'
import { CodeMultiPicker } from './CodeMultiPicker'
import { ProofParamsEditor, RecipientParamsEditor } from './StepParamsEditors'
import { hasCondition, useEditorOptions, type EditorOptions } from './editor-options'
import { ConditionBuilder, stripInternalFields } from '../derivations/ConditionBuilder'
import {
    labelIn,
    useCloneScenario,
    useCreateScenario,
    useDraftPreview,
    useScenarioVocabulary,
    useScenarios,
    useUpdateScenario,
    type ApiError,
    type ScenarioLintError,
} from '../../hooks/useScenariosApi'

type I18n = Record<string, string>
type DueSource = { contract?: string; rule?: string; param?: string; days?: number; date?: string; minus?: DueSource }
type Step = {
    code: string
    label: I18n
    action?: string
    subtype?: string
    recipient?: { resolver: string; params?: Record<string, unknown>; fallback?: unknown }
    message?: { subject: I18n; body: I18n }
    after?: string[]
    if_timeout?: string
    if_failed?: string
    when?: Record<string, unknown>
    due?: DueSource[]
    wait_for?: Record<string, unknown> & { kind?: string }
    sub_scenario?: string
    delegable?: boolean
    [key: string]: unknown
}
type Draft = {
    code?: string
    label: I18n
    description?: I18n
    universes?: string[]
    trigger: { subtypes?: string[]; rule_codes?: string[]; rule_classes?: string[]; target_asset_types?: string[]; when?: unknown; auto_start?: boolean }
    proof?: Record<string, unknown>
    measures?: unknown[]
    steps: Step[]
}

const EMPTY: Draft = { label: { fr: '', en: '' }, trigger: {}, steps: [] }


/** The vocabulary, plus the current value when the vocabulary lacks it. */
function withCurrent(list: string[], current?: string): string[] {
    return current && !list.includes(current) ? [...list, current] : list
}

function parseJSON(text: string): Record<string, unknown> | undefined {
    if (!text.trim()) return undefined
    try {
        return JSON.parse(text)
    } catch {
        return undefined
    }
}

/** Drops the condition builder's internal fields from a step before it is saved. */
function cleanStep(step: Step): Step {
    if (!step.wait_for || step.wait_for.kind !== 'property_holds' || !step.wait_for.condition) return step
    return { ...step, wait_for: { ...step.wait_for, condition: stripInternalFields(step.wait_for.condition) } }
}

/** The scenario editor (spec 2026-10-28-15 § 10). */
export function ScenarioEditorPage() {
    const { org, code } = useParams({ strict: false }) as { org: string; code: string }
    const { t, i18n } = useTranslation('scenarios')
    const navigate = useNavigate()
    const canWrite = useCan('compliance.write')
    const { data: list = [], isLoading } = useScenarios(org)
    const { data: vocab } = useScenarioVocabulary(org)
    const row = list.find((s) => s.code === code)
    const isNew = code === 'new'
    const readOnly = !canWrite || row?.origin === 'shipped'
    const create = useCreateScenario(org)
    const update = useUpdateScenario(org)
    const clone = useCloneScenario(org)
    const preview = useDraftPreview(org)
    const options = useEditorOptions(org, list)
    const [draft, setDraft] = useState<Draft>(EMPTY)
    const [errors, setErrors] = useState<ScenarioLintError[]>([])
    const [saved, setSaved] = useState(false)
    const [testIssue, setTestIssue] = useState('')
    const [testOpen, setTestOpen] = useState(false)

    useBreadcrumbs([
        { title: t('common:nav.home'), path: `/${org}` },
        { title: t('title'), path: `/${org}/remediation-scenarios` },
        { title: row ? labelIn(row.label, i18n.language, row.code) : t('actions.new') },
    ])

    useEffect(() => {
        if (!row) return
        const def = (row.definition ?? {}) as { proof?: Record<string, unknown>; measures?: unknown[]; steps?: Step[] }
        setDraft({
            code: row.code,
            label: row.label ?? {},
            description: row.description ?? {},
            universes: row.universes ?? [],
            trigger: (row.trigger ?? {}) as Draft['trigger'],
            proof: def.proof,
            measures: def.measures,
            steps: def.steps ?? [],
        })
    }, [row])

    const stepCodes = useMemo(() => draft.steps.map((s) => s.code), [draft.steps])
    const set = (patch: Partial<Draft>) => {
        setDraft((d) => ({ ...d, ...patch }))
        setSaved(false)
    }
    const setTrigger = (patch: Partial<Draft['trigger']>) => set({ trigger: { ...draft.trigger, ...patch } })
    const setStep = (index: number, patch: Partial<Step>) =>
        set({ steps: draft.steps.map((s, i) => (i === index ? { ...s, ...patch } : s)) })
    const move = (index: number, delta: number) => {
        const steps = [...draft.steps]
        const target = index + delta
        if (target < 0 || target >= steps.length) return
        ;[steps[index], steps[target]] = [steps[target], steps[index]]
        set({ steps })
    }

    const body = () => ({
        label: draft.label,
        description: draft.description,
        universes: draft.universes,
        trigger: { ...draft.trigger, when: hasCondition(draft.trigger.when) ? stripInternalFields(draft.trigger.when) : undefined },
        proof: draft.proof,
        measures: draft.measures ?? [],
        steps: draft.steps.map(cleanStep),
    })

    const save = () => {
        setErrors([])
        const onError = (e: unknown) => setErrors((e as ApiError).errors ?? [{ field: '', code: (e as ApiError).code ?? 'error', message: (e as ApiError).title ?? '' }])
        if (isNew) {
            create.mutate(body(), {
                onSuccess: (s) => navigate({ to: '/$org/remediation-scenarios/$code', params: { org, code: s.code } }),
                onError,
            })
        } else if (row) {
            update.mutate({ uid: row.uid, body: body() }, { onSuccess: () => setSaved(true), onError })
        }
    }

    const errorsOf = (step?: string) => errors.filter((e) => (step ? e.step === step : !e.step))

    if (isLoading) return null
    if (!row && !isNew) return <p className="text-sm text-muted-foreground">{t('list.empty')}</p>

    return (
        <div className="space-y-4" data-testid="scenario-editor">
            <PageHeader
                icon={mdiSitemapOutline}
                title={labelIn(draft.label, i18n.language, draft.code ?? t('actions.new'))}
                description={row ? `${draft.code} · ${t('editor.version', { version: row.version })}` : undefined}
                actions={
                    <div className="flex flex-wrap gap-2">
                        {row && canWrite && (
                            <Button
                                variant="outline"
                                onClick={() => clone.mutate({ uid: row.uid }, { onSuccess: (c) => navigate({ to: '/$org/remediation-scenarios/$code', params: { org, code: c.code } }) })}
                                data-testid="scenario-editor-clone"
                            >
                                <AppIcon path={mdiContentCopy} size="sm" />
                                {t('actions.clone')}
                            </Button>
                        )}
                        {!isNew && (
                            <Button variant="outline" onClick={() => setTestOpen(true)} data-testid="scenario-test-on-ticket">
                                <AppIcon path={mdiFlask} size="sm" />
                                {t('actions.test_on_ticket')}
                            </Button>
                        )}
                        {!readOnly && (
                            <Button onClick={save} disabled={create.isPending || update.isPending} data-testid="scenario-save">
                                <AppIcon path={mdiContentSave} size="sm" />
                                {t('actions.save')}
                            </Button>
                        )}
                    </div>
                }
            />
            {row?.origin === 'shipped' && <p className="text-sm text-muted-foreground" data-testid="scenario-read-only">{t('editor.read_only')}</p>}
            {saved && (
                <p className="text-sm text-muted-foreground" data-testid="scenario-saved">
                    {t('editor.saved')}
                    {row && row.running_count > 0 && ` · ${t('editor.pinned_runs', { count: row.running_count, version: row.version - 1 })}`}
                </p>
            )}
            {errors.length > 0 && (
                <div className="rounded border border-destructive p-2 text-sm text-destructive" data-testid="scenario-validate-errors">
                    <p className="font-medium">{t('editor.lint_errors')}</p>
                    <ul className="list-disc pl-5">
                        {errors.map((e, i) => <li key={i} data-testid={`scenario-validate-error-${e.code}`}>{e.step ? `${e.step} · ` : ''}{e.message}</li>)}
                    </ul>
                </div>
            )}
            <fieldset disabled={readOnly} className="space-y-4">
                <Card>
                    <CardHeader><CardTitle className="text-base">{t('editor.trigger')}</CardTitle></CardHeader>
                    <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <Input value={draft.label.fr ?? ''} onChange={(e) => set({ label: { ...draft.label, fr: e.target.value } })} placeholder={`${t('editor.label')} (FR)`} data-testid="scenario-label-fr" />
                        <Input value={draft.label.en ?? ''} onChange={(e) => set({ label: { ...draft.label, en: e.target.value } })} placeholder={`${t('editor.label')} (EN)`} data-testid="scenario-label-en" />
                        <Textarea value={draft.description?.fr ?? ''} onChange={(e) => set({ description: { ...draft.description, fr: e.target.value } })} placeholder={`${t('editor.description')} (FR)`} />
                        <Textarea value={draft.description?.en ?? ''} onChange={(e) => set({ description: { ...draft.description, en: e.target.value } })} placeholder={`${t('editor.description')} (EN)`} />
                        <CodeMultiPicker value={draft.universes ?? []} options={options.universes} onChange={(universes) => set({ universes })} placeholder={t('editor.universes')} disabled={readOnly} testId="scenario-universes" />
                        <CodeMultiPicker value={draft.trigger.subtypes ?? []} options={options.subtypes} onChange={(subtypes) => setTrigger({ subtypes })} placeholder={t('editor.subtypes')} disabled={readOnly} testId="scenario-trigger-subtypes" />
                        <CodeMultiPicker value={draft.trigger.rule_codes ?? []} options={options.rules} onChange={(rule_codes) => setTrigger({ rule_codes })} placeholder={t('editor.rule_codes')} disabled={readOnly} testId="scenario-trigger-rules" />
                        <CodeMultiPicker value={draft.trigger.rule_classes ?? []} options={options.ruleClasses} onChange={(rule_classes) => setTrigger({ rule_classes })} placeholder={t('editor.rule_classes')} disabled={readOnly} testId="scenario-trigger-rule-classes" />
                        <CodeMultiPicker value={draft.trigger.target_asset_types ?? []} options={options.assetTypes} onChange={(target_asset_types) => setTrigger({ target_asset_types })} placeholder={t('editor.asset_types')} disabled={readOnly} testId="scenario-trigger-asset-types" />
                        <label className="flex items-center gap-2 text-sm">
                            <Checkbox checked={!!draft.trigger.auto_start} onCheckedChange={(c) => setTrigger({ auto_start: c === true })} data-testid="scenario-auto-start" />
                            {t('editor.auto_start')}
                        </label>
                        <div className="space-y-1 md:col-span-2" data-testid="scenario-trigger-when">
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground">{t('editor.trigger_when')}</span>
                                {!readOnly && (hasCondition(draft.trigger.when) ? (
                                    <Button type="button" size="sm" variant="ghost" onClick={() => setTrigger({ when: undefined })} data-testid="scenario-trigger-when-remove">{t('editor.remove_condition')}</Button>
                                ) : (
                                    <Button type="button" size="sm" variant="ghost" onClick={() => setTrigger({ when: { all: [] } })} data-testid="scenario-trigger-when-add">{t('editor.add_condition')}</Button>
                                ))}
                            </div>
                            {draft.trigger.when !== undefined && draft.trigger.when !== null && (
                                <ConditionBuilder conditions={draft.trigger.when} onChange={(when) => setTrigger({ when })} org={org} defaultDateMode="relative" />
                            )}
                        </div>
                    </CardContent>
                </Card>
                {draft.steps.map((st, index) => (
                    <StepCard
                        key={index}
                        index={index}
                        step={st}
                        earlier={stepCodes.slice(0, index)}
                        allCodes={stepCodes}
                        scenarios={list.map((s) => s.code)}
                        vocab={vocab}
                        options={options}
                        org={org}
                        errors={errorsOf(st.code)}
                        readOnly={readOnly}
                        onChange={(patch) => setStep(index, patch)}
                        onMove={(d) => move(index, d)}
                        onRemove={() => set({ steps: draft.steps.filter((_, i) => i !== index) })}
                    />
                ))}
                {!readOnly && (
                    <Button
                        variant="outline"
                        onClick={() => set({ steps: [...draft.steps, { code: `step_${draft.steps.length + 1}`, label: { fr: '', en: '' }, action: 'internal_task', recipient: { resolver: 'internal' } }] })}
                        data-testid="scenario-step-add"
                    >
                        <AppIcon path={mdiPlus} size="sm" />
                        {t('actions.add_step')}
                    </Button>
                )}
            </fieldset>
            <Dialog open={testOpen} onOpenChange={setTestOpen}>
                <DialogContent className="sm:max-w-2xl" data-testid="scenario-test-dialog">
                    <DialogHeader><DialogTitle>{t('actions.test_on_ticket')}</DialogTitle></DialogHeader>
                    <div className="flex gap-2">
                        <Input value={testIssue} onChange={(e) => setTestIssue(e.target.value)} placeholder={t('editor.test_issue')} data-testid="scenario-test-issue" />
                        <Button onClick={() => preview.mutate({ issue_uid: testIssue.trim(), scenario: { code: draft.code, ...body() } })} disabled={!testIssue.trim()} data-testid="scenario-test-run">
                            {t('ticket.start')}
                        </Button>
                    </div>
                    {preview.data && <PreviewSteps preview={preview.data} orgCode={org} />}
                </DialogContent>
            </Dialog>
        </div>
    )
}

type Vocab = ReturnType<typeof useScenarioVocabulary>['data']

function StepCard({ index, step, earlier, allCodes, scenarios, vocab, options, org, errors, readOnly, onChange, onMove, onRemove }: {
    index: number
    step: Step
    earlier: string[]
    allCodes: string[]
    scenarios: string[]
    vocab: Vocab
    options: EditorOptions
    org: string
    errors: ScenarioLintError[]
    readOnly: boolean
    onChange: (patch: Partial<Step>) => void
    onMove: (delta: number) => void
    onRemove: () => void
}) {
    const { t, i18n } = useTranslation('scenarios')
    const { t: ti } = useTranslation('issues')
    const activation = step.after?.length ? 'after' : step.if_timeout ? 'if_timeout' : step.if_failed ? 'if_failed' : 'at_start'
    const activationTarget = step.after?.[0] ?? step.if_timeout ?? step.if_failed ?? ''
    const setActivation = (mode: string, target: string) => {
        const patch: Partial<Step> = { after: undefined, if_timeout: undefined, if_failed: undefined }
        if (mode === 'after' && target) patch.after = [target]
        if (mode === 'if_timeout' && target) patch.if_timeout = target
        if (mode === 'if_failed' && target) patch.if_failed = target
        onChange(patch)
    }
    const [whenText, setWhenText] = useState(step.when ? JSON.stringify(step.when) : '')
    const due = step.due ?? []
    const setDue = (i: number, src: DueSource | null) => {
        const next = [...due]
        if (src) next[i] = src
        else next.splice(i, 1)
        onChange({ due: next })
    }
    const ruleParams = (code?: string) => Object.keys(vocab?.due_rules?.find((x) => x.code === code)?.params ?? {}).sort()
    const ruleValue = (src: DueSource) => {
        const r = vocab?.due_rules?.find((x) => x.code === src.rule)
        const v = r?.params?.[src.param ?? '']
        if (v == null) return ''
        if (typeof v === 'object') {
            const d = v as { value?: unknown; unit?: string }
            return d.value != null ? `${d.value} ${(d.unit ?? '').toLowerCase()}`.trim() : JSON.stringify(v)
        }
        return String(v)
    }
    const known = new Set(allCodes)
    return (
        <Card data-testid={`scenario-step-${index}`}>
            <CardHeader className="flex flex-row items-center gap-2 pb-2">
                <CardTitle className="flex-1 text-base">
                    {index + 1}. {labelIn(step.label, i18n.language, step.code)}
                    <Badge variant="outline" className="ml-2 text-[10px]">{step.code}</Badge>
                </CardTitle>
                {!readOnly && (
                    <>
                        <Button size="icon" variant="ghost" onClick={() => onMove(-1)} aria-label={t('actions.up')} data-testid={`scenario-step-up-${index}`}><AppIcon path={mdiArrowUp} size="sm" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => onMove(1)} aria-label={t('actions.down')} data-testid={`scenario-step-down-${index}`}><AppIcon path={mdiArrowDown} size="sm" /></Button>
                        <Button size="icon" variant="ghost" onClick={onRemove} aria-label={t('actions.remove')} data-testid={`scenario-step-remove-${index}`}><AppIcon path={mdiDelete} size="sm" /></Button>
                    </>
                )}
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-2 md:grid-cols-2">
                <Input value={step.code} onChange={(e) => onChange({ code: e.target.value })} placeholder={t('editor.step_code')} data-testid={`scenario-step-code-${index}`} />
                <select className="h-9 rounded border border-border bg-background px-2 text-sm" value={step.action ?? ''} onChange={(e) => onChange({ action: e.target.value })} data-testid={`scenario-step-action-${index}`}>
                    {(vocab?.actions ?? []).map((a) => <option key={a.code} value={a.code}>{ti(`remediation.action.${a.code}`, { defaultValue: a.code })}</option>)}
                </select>
                <Input value={step.label.fr ?? ''} onChange={(e) => onChange({ label: { ...step.label, fr: e.target.value } })} placeholder={`${t('editor.label')} (FR)`} />
                <Input value={step.label.en ?? ''} onChange={(e) => onChange({ label: { ...step.label, en: e.target.value } })} placeholder={`${t('editor.label')} (EN)`} />
                <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">{t('editor.resolver')}</label>
                    <select
                        className="h-9 w-full rounded border border-border bg-background px-2 text-sm"
                        value={step.recipient?.resolver ?? 'internal'}
                        onChange={(e) => onChange({ recipient: { ...(step.recipient ?? {}), resolver: e.target.value } })}
                        data-testid={`scenario-step-resolver-${index}`}
                    >
                        {(vocab?.resolvers ?? []).map((r) => <option key={r} value={r}>{t(`resolvers.${r}`, { defaultValue: r })}</option>)}
                    </select>
                    <RecipientParamsEditor
                        resolver={step.recipient?.resolver ?? 'internal'}
                        params={step.recipient?.params}
                        onChange={(params) => onChange({ recipient: { ...(step.recipient ?? { resolver: 'internal' }), params } })}
                        vocab={vocab}
                        options={options}
                        steps={allCodes.filter((c) => c !== step.code)}
                        index={index}
                    />
                </div>
                <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">{t('editor.activation')}</label>
                    <div className="flex gap-1">
                        <select className="h-9 flex-1 rounded border border-border bg-background px-2 text-sm" value={activation} onChange={(e) => setActivation(e.target.value, activationTarget || earlier[earlier.length - 1] || '')} data-testid={`scenario-step-activation-${index}`}>
                            <option value="at_start">{t('editor.at_start')}</option>
                            <option value="after">{t('editor.after')}</option>
                            <option value="if_timeout">{t('editor.if_timeout')}</option>
                            <option value="if_failed">{t('editor.if_failed')}</option>
                        </select>
                        {activation !== 'at_start' && (
                            <select className="h-9 flex-1 rounded border border-border bg-background px-2 text-sm" value={activationTarget} onChange={(e) => setActivation(activation, e.target.value)} data-testid={`scenario-step-activation-target-${index}`}>
                                {[...earlier, ...(activationTarget && !earlier.includes(activationTarget) && known.has(activationTarget) ? [activationTarget] : [])].map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        )}
                    </div>
                    <Input value={whenText} onChange={(e) => { setWhenText(e.target.value); onChange({ when: parseJSON(e.target.value) }) }} placeholder={t('editor.when')} className="font-mono text-xs" />
                </div>
                <div className="space-y-1 md:col-span-2" data-testid={`scenario-step-due-${index}`}>
                    <label className="text-xs text-muted-foreground">{t('editor.due')}</label>
                    {due.map((src, i) => {
                        const kind = src.contract !== undefined ? 'contract' : src.rule !== undefined ? 'rule' : src.days !== undefined ? 'days' : 'date'
                        return (
                            <div key={i} className="flex flex-wrap items-center gap-1">
                                <select
                                    className="h-8 rounded border border-border bg-background px-2 text-xs"
                                    value={kind}
                                    onChange={(e) => setDue(i, e.target.value === 'contract' ? { contract: 'intervention_time' } : e.target.value === 'rule' ? { rule: 'SCENARIO_DELAYS_DEFAULT', param: 'repair_days' } : e.target.value === 'days' ? { days: 5 } : { date: 'dossier.due_date' })}
                                    data-testid={`scenario-step-due-kind-${index}-${i}`}
                                >
                                    <option value="contract">{t('editor.due_contract')}</option>
                                    <option value="rule">{t('editor.due_rule')}</option>
                                    <option value="days">{t('editor.due_days')}</option>
                                    <option value="date">{t('editor.date_source')}</option>
                                </select>
                                {kind === 'contract' && (
                                    <select className="h-8 rounded border border-border bg-background px-2 text-xs" value={src.contract} onChange={(e) => setDue(i, { contract: e.target.value })} data-testid={`scenario-step-due-contract-${index}-${i}`}>
                                        {withCurrent(vocab?.contract_clauses ?? [], src.contract).map((c) => <option key={c} value={c}>{t(`editor.clauses.${c}`, { defaultValue: c })}</option>)}
                                    </select>
                                )}
                                {kind === 'rule' && (
                                    <>
                                        <select className="h-8 rounded border border-border bg-background px-2 text-xs" value={src.rule} onChange={(e) => setDue(i, { rule: e.target.value, param: ruleParams(e.target.value)[0] ?? '' })} data-testid={`scenario-step-due-rule-${index}-${i}`}>
                                            {withCurrent((vocab?.due_rules ?? []).map((r) => r.code ?? '').filter(Boolean), src.rule).map((c) => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                        <select className="h-8 rounded border border-border bg-background px-2 text-xs" value={src.param} onChange={(e) => setDue(i, { ...src, param: e.target.value })} data-testid={`scenario-step-due-param-${index}-${i}`}>
                                            {withCurrent(ruleParams(src.rule), src.param).map((c) => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                        <span className="text-xs text-muted-foreground">{t('editor.current_value', { value: ruleValue(src) || '—' })}</span>
                                        {src.rule && options.ruleUids[src.rule] && (
                                            <Link to="/$org/compliance-rules/$ruleUid" params={{ org, ruleUid: options.ruleUids[src.rule] }} className="text-xs text-primary hover:underline" data-testid={`scenario-step-due-rule-link-${index}-${i}`}>
                                                {t('editor.open_rule')}
                                            </Link>
                                        )}
                                    </>
                                )}
                                {kind === 'days' && <Input type="number" className="h-8 w-24 text-xs" value={src.days} onChange={(e) => setDue(i, { days: Number(e.target.value) })} data-testid={`scenario-step-due-days-${index}-${i}`} />}
                                {kind === 'date' && (
                                    <select className="h-8 rounded border border-border bg-background px-2 text-xs" value={src.date} onChange={(e) => setDue(i, { ...src, date: e.target.value })} data-testid={`scenario-step-due-date-${index}-${i}`}>
                                        {withCurrent(vocab?.date_sources ?? [], src.date).map((c) => <option key={c} value={c}>{t(`editor.date_sources.${c}`, { defaultValue: c })}</option>)}
                                    </select>
                                )}
                                {!readOnly && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setDue(i, null)} aria-label={t('actions.remove')}><AppIcon path={mdiDelete} size="sm" /></Button>}
                            </div>
                        )
                    })}
                    {!readOnly && (
                        <Button size="sm" variant="ghost" onClick={() => onChange({ due: [...due, { days: 5 }] })} data-testid={`scenario-step-due-add-${index}`}>
                            <AppIcon path={mdiPlus} size="sm" />
                        </Button>
                    )}
                </div>
                <div className="space-y-1 md:col-span-2">
                    <label className="text-xs text-muted-foreground">{t('editor.proof')}</label>
                    <div className="flex gap-1">
                        <select
                            className="h-9 rounded border border-border bg-background px-2 text-sm"
                            value={step.wait_for?.kind ?? ''}
                            onChange={(e) => onChange({ wait_for: e.target.value ? { kind: e.target.value } : undefined })}
                            data-testid={`scenario-step-proof-${index}`}
                        >
                            <option value="">{t('editor.none')}</option>
                            {(vocab?.proofs ?? []).map((p) => <option key={p} value={p}>{ti(`proof.${p}`, { defaultValue: p })}</option>)}
                        </select>
                    </div>
                    <ProofParamsEditor
                        proof={step.wait_for}
                        onChange={(wait_for) => onChange({ wait_for })}
                        options={options}
                        steps={allCodes.filter((c) => c !== step.code)}
                        org={org}
                        index={index}
                    />
                </div>
                <Input value={step.message?.subject?.fr ?? ''} onChange={(e) => onChange({ message: { subject: { ...(step.message?.subject ?? {}), fr: e.target.value }, body: step.message?.body ?? {} } })} placeholder={`${t('editor.message_subject')} (FR)`} />
                <Input value={step.message?.subject?.en ?? ''} onChange={(e) => onChange({ message: { subject: { ...(step.message?.subject ?? {}), en: e.target.value }, body: step.message?.body ?? {} } })} placeholder={`${t('editor.message_subject')} (EN)`} />
                <Textarea value={step.message?.body?.fr ?? ''} onChange={(e) => onChange({ message: { subject: step.message?.subject ?? {}, body: { ...(step.message?.body ?? {}), fr: e.target.value } } })} placeholder={`${t('editor.message_body')} (FR)`} />
                <Textarea value={step.message?.body?.en ?? ''} onChange={(e) => onChange({ message: { subject: step.message?.subject ?? {}, body: { ...(step.message?.body ?? {}), en: e.target.value } } })} placeholder={`${t('editor.message_body')} (EN)`} />
                <select className="h-9 rounded border border-border bg-background px-2 text-sm" value={step.sub_scenario ?? ''} onChange={(e) => onChange({ sub_scenario: e.target.value || undefined })}>
                    <option value="">{t('editor.sub_scenario')} : {t('editor.none')}</option>
                    {scenarios.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={!!step.delegable} onCheckedChange={(c) => onChange({ delegable: c === true })} />
                    {t('editor.delegable')}
                </label>
                {errors.length > 0 && (
                    <ul className="text-xs text-destructive md:col-span-2" data-testid={`scenario-step-errors-${index}`}>
                        {errors.map((e, i) => <li key={i}>{e.message}</li>)}
                    </ul>
                )}
            </CardContent>
        </Card>
    )
}
